/**
 * Aureus Social Pro — Moteur Flexi-jobs
 * =====================================
 *
 * Calcul complet de la paie flexi-job belge (CP 302 Horeca + tous secteurs
 * éligibles au régime flexi-job).
 *
 * Source des constantes : app/lib/lois-belges-flexi.js
 * Dernière mise à jour des constantes : 2026-03-01
 *
 * Règles couvertes :
 *  - Cotisation patronale spéciale 28% (ONSS)
 *  - Exonération ONSS + PP travailleur (0%)
 *  - Flexi-pécule de vacances 7,67%
 *  - Plafond annuel non-pensionnés (loi 30/12/2025) — valeur dans lois-belges.js
 *  - Plafond pension anticipée 8 121€ (2026)
 *  - Éligibilité 4/5 temps T-3 (fallback T-5)
 *  - Interdiction entreprise liée
 *  - Flexi-salaire min/max par CP — lu dans lois-belges.js, jamais écrit ici
 *    (ce commentaire annonçait « 11,87€/h au 01/03/2026 » : périmé au
 *     01/09/2026, et un commentaire périmé se lit comme un fait établi)
 *  - Plafond flexi-salaire : horeca = montant horaire fixe indexé ; ailleurs
 *    150 % du barème sectoriel (ONSS, page employeurs flexi-jobs, 23/09/2026)
 *
 * NON couvert dans ce module (scope ultérieur) :
 *  - Génération Dimona FLEX (module Dimona)
 *  - Enregistrement présence SCE/SAE (module SCE)
 *  - Déclaration DmfA codes travailleurs flexi (module DmfA)
 *  - Fiche 281.10 code 250 pour dépassement plafond (module Belcotax)
 *  - Bonus CCT 90 appliqué à flexi (module primes)
 *  - Extension "tous secteurs" 2026-07-01 (à activer à la date)
 */

import { LB_FLEXI } from './lois-belges-flexi'
import { estFlexiRegime } from '@/app/lib/regimes-speciaux';
import { round2 } from '@/app/lib/money';
import { CODES_PAR_REGIME } from '@/app/lib/code-travailleur-dmfa';

/* ============================================================
 * 1. ÉLIGIBILITÉ
 * ============================================================ */

/**
 * Vérifie l'éligibilité d'un travailleur au régime flexi-job.
 *
 * @param {Object} travailleur
 * @param {boolean} [travailleur.pensionne=false] - Retraité (âge légal atteint)
 * @param {boolean} [travailleur.pensionAnticipee=false] - Pension anticipée (plafond 8 121€)
 * @param {number}  [travailleur.tauxEmploiT3=0] - Taux d'emploi au trimestre T-3 (0..1)
 * @param {number|null} [travailleur.tauxEmploiT5=null] - Fallback T-5 si T-3 < 80%
 * @param {boolean} [travailleur.memeEmployeurOuLie=false] - Flexi chez employeur principal ou entreprise liée
 * @param {boolean} [travailleur.intérimaire=false] - Est travailleur intérimaire
 * @param {boolean} [travailleur.memeUtilisateurIntérim=false] - Même utilisateur intérim/flexi
 * @returns {{eligible: boolean, raison: string, plafondAnnuel?: number, regimeSpecifique?: string}}
 */
/**
 * Le travailleur est-il en FLEXI-JOB ? — source unique de la détection.
 *
 * ⚠️ Née le 14/08/2026, en même temps que `estContratEtudiant` et pour la MÊME
 * raison : la porte flexi de la route DmfA devinait le régime sur une liste de
 * champs (`emp.type`, `data.contractType`, `data.regime`…) qu'une fiche encodée
 * normalement ne porte pas. L'écran fiche employé range « flexijob » dans
 * `data.statut` — jamais lu. Un flexi serait donc parti en ONSS PLEINE au lieu
 * de son régime propre (cotisation patronale spéciale, exonération de
 * cotisation personnelle).
 *
 * ⚠️ Ne PAS lire `emp.statut` : à ce stade il a été RE-DÉRIVÉ de la commission
 * paritaire (employé/ouvrier) et ne porte plus le régime spécial. La valeur
 * d'origine survit dans le JSONB `data`.
 */
export function estContratFlexi(emp = {}) {
  return estFlexiRegime(emp);
}

export function verifEligibiliteFlexi(travailleur = {}) {
  const {
    pensionne = false,
    pensionAnticipee = false,
    tauxEmploiT3 = 0,
    tauxEmploiT5 = null,
    memeEmployeurOuLie = false,
    intérimaire = false,
    memeUtilisateurIntérim = false,
  } = travailleur

  // Règle 1 : interdiction employeur principal / entreprise liée (loi 2024)
  if (memeEmployeurOuLie) {
    return {
      eligible: false,
      raison:
        "Interdiction : flexi-job chez l'employeur principal ou une entreprise liée (loi 2024)",
    }
  }

  // Règle 2 : interdiction intérim chez même utilisateur (loi 2024)
  if (intérimaire && memeUtilisateurIntérim) {
    return {
      eligible: false,
      raison:
        "Interdiction : intérimaire ne peut être flexi chez le même utilisateur dans le même trimestre",
    }
  }

  // Règle 3 : pensionnés éligibles sans condition de taux d'emploi
  if (pensionne) {
    if (pensionAnticipee) {
      return {
        eligible: true,
        raison: `Pensionné (pension anticipée) — plafond ${LB_FLEXI.plafondAnnuelPensionnesAnticipes}€/an`,
        plafondAnnuel: LB_FLEXI.plafondAnnuelPensionnesAnticipes,
        regimeSpecifique: 'pension_anticipee',
      }
    }
    return {
      eligible: true,
      raison: "Pensionné (âge légal) — pas de plafond annuel",
      plafondAnnuel: null,
      regimeSpecifique: 'pension_normale',
    }
  }

  // Règle 4 : non-pensionnés — 4/5 temps au T-3 (fallback T-5)
  const seuil = LB_FLEXI.eligibilite.tauxEmploiMin

  if (tauxEmploiT3 >= seuil) {
    return {
      eligible: true,
      raison: `Taux d'emploi T-3 = ${(tauxEmploiT3 * 100).toFixed(0)}% ≥ ${seuil * 100}%`,
      plafondAnnuel: LB_FLEXI.plafondAnnuelNonPensionnes,
    }
  }

  if (tauxEmploiT5 !== null && tauxEmploiT5 >= seuil) {
    return {
      eligible: true,
      raison: `Taux d'emploi T-5 = ${(tauxEmploiT5 * 100).toFixed(0)}% ≥ ${seuil * 100}% (fallback)`,
      plafondAnnuel: LB_FLEXI.plafondAnnuelNonPensionnes,
    }
  }

  return {
    eligible: false,
    raison: `Taux d'emploi T-3 (${(tauxEmploiT3 * 100).toFixed(0)}%) < ${seuil * 100}% requis`,
  }
}

/* ============================================================
 * 2. VALIDATION DU FLEXI-SALAIRE
 * ============================================================ */

/**
 * Valide le flexi-salaire horaire selon le secteur et les règles légales.
 *
 * @param {number} flexiSalaireHoraire - € / heure proposé
 * @param {string} cp - Commission paritaire ("302", "201", etc.)
 * @param {Object} [options]
 * @param {number} [options.salaireBaremaireHoraire] - Barème sectoriel de la fonction (€/h)
 * @param {number} [options.rmmmgHoraire] - RMMMG horaire (fallback si pas de barème)
 * @returns {{valide: boolean, raison?: string, min?: number, max?: number}}
 */
export function validerFlexiSalaire(flexiSalaireHoraire, cp, options = {}) {
  if (typeof flexiSalaireHoraire !== 'number' || flexiSalaireHoraire <= 0) {
    return { valide: false, raison: 'Flexi-salaire horaire invalide (doit être > 0)' }
  }

  // Horeca : minimum ET maximum horaires fixes, indexés.
  //
  // ⚠️ Le maximum horaire fixe ne vaut QUE pour l'horeca — corrigé le
  // 23/09/2026. Ce contrôle s'appliquait à toutes les CP, avant le test de la
  // CP 302. Page ONSS employeurs « flexi-jobs » (lue par le runner lire-source
  // le 23/09/2026) : « Le secteur de l'horeca est soumis à des règles
  // spécifiques. Contrairement aux autres secteurs, le plafond du flexi-salaire
  // y est fixé sous la forme d'un montant horaire maximum indexé. » Ailleurs,
  // la limite est 150 % de la rémunération de base minimale (plus bas).
  // Conséquence réelle : en CP 149.01 le barème de la catégorie E (22,28 €/h
  // au 01/01/2026) dépasse le maximum horeca — le contrôle refusait donc un
  // flexi-salaire que la loi IMPOSE.
  if (cp === '302') {
    if (flexiSalaireHoraire > LB_FLEXI.plafondMaxHoraire) {
      return {
        valide: false,
        raison: `Flexi-salaire Horeca > plafond légal ${LB_FLEXI.plafondMaxHoraire}€/h`,
        max: LB_FLEXI.plafondMaxHoraire,
      }
    }
    const minHoreca = LB_FLEXI.horeca.salaireHoraireMin
    if (flexiSalaireHoraire < minHoreca) {
      return {
        valide: false,
        raison: `Flexi-salaire Horeca < ${minHoreca}€/h (minimum légal au ${LB_FLEXI.horeca.dateEffet})`,
        min: minHoreca,
        max: LB_FLEXI.plafondMaxHoraire,
      }
    }
    return { valide: true, min: minHoreca, max: LB_FLEXI.plafondMaxHoraire }
  }

  // Autres secteurs : barème sectoriel OU RMMMG
  const { salaireBaremaireHoraire, rmmmgHoraire } = options
  const minRequis = salaireBaremaireHoraire || rmmmgHoraire

  if (!minRequis) {
    return {
      valide: false,
      raison:
        'Options manquantes : salaireBaremaireHoraire OU rmmmgHoraire requis pour CP hors 302',
    }
  }

  if (flexiSalaireHoraire < minRequis) {
    return {
      valide: false,
      raison: `Flexi-salaire < salaire minimum sectoriel/RMMMG (${minRequis}€/h)`,
      min: minRequis,
    }
  }

  // Plafond 150% barème sectoriel (si fourni)
  if (salaireBaremaireHoraire) {
    const plafond150 = round2(salaireBaremaireHoraire * LB_FLEXI.plafondMaxPourcentageBareme)
    if (flexiSalaireHoraire > plafond150) {
      return {
        valide: false,
        raison: `Flexi-salaire > 150% barème sectoriel (${plafond150}€/h)`,
        max: plafond150,
      }
    }
  }

  return {
    valide: true,
    min: minRequis,
    max: salaireBaremaireHoraire
      ? round2(salaireBaremaireHoraire * LB_FLEXI.plafondMaxPourcentageBareme)
      : null,
  }
}

/* ============================================================
 * 3. CALCUL DE PAIE FLEXI
 * ============================================================ */

/**
 * LA règle du coût flexi, écrite UNE fois (R14 bis) et importée partout.
 *
 * ⚠️ Pourquoi elle sort d'ici (23/09/2026) : `calcPayrollFlexi` faisait déjà le
 * bon calcul, mais QUATRE autres surfaces le refaisaient à la main — et trois
 * appliquaient les 28 % au flexi-salaire SEUL, en présentant le pécule comme
 * « versé annuellement » (payroll-engine `calcPayrollStatut('flexijob')`,
 * simulateur de RegimesSpeciaux, fiche de PrimesAvantagesV2). Écart mesuré sur
 * 800 € : 241,18 € de cotisation au lieu de 224,00 €.
 *
 * Mécanique :
 *  - le flexi-pécule (7,67 %) s'ajoute au flexi-salaire et est PAYÉ AVEC LUI ;
 *  - le travailleur reçoit salaire + pécule, sans ONSS ni précompte ;
 *  - la cotisation patronale spéciale (28 %) porte sur salaire + pécule.
 * Taux : LB_FLEXI (→ lois-belges.js `cotisations.flexiJob`), jamais ici.
 * ✅ Source PRIMAIRE : instructions administratives ONSS 2026/3,
 * socialsecuritycontributions/calculationbase/flexi/flexi_and_overtime_horeca.html,
 * lue par le runner `lire-source.yml` le 23/09/2026 — sondé le 23/09/2026
 * (21 variantes, verdict trouve). Mot pour mot : « un simple pécule de vacances
 * de 7,67 % (flexi-pécule de vacances), qui est payé ensemble avec le salaire »
 * et « La cotisation de 28,00 % s'applique au salaire complet (y compris le
 * pécule de vacances) ».
 * ⚠️ Une cotisation SECTORIELLE éventuelle (fonds de sécurité d'existence,
 * pension sectorielle) n'est PAS comprise ici : elle dépend de la CCT du
 * secteur, pas de la loi flexi.
 *
 * @param {number} flexiSalaireBrut flexi-salaire de la période, pécule NON compris
 */
export function coutFlexiDepuisSalaire(flexiSalaireBrut) {
  const salaire = round2(Number(flexiSalaireBrut) || 0)
  const flexiPecule = round2(salaire * LB_FLEXI.pecuelVacancesRatio)
  const baseONSSPatronale = round2(salaire + flexiPecule)
  const cotisationONSSpatronale = round2(baseONSSPatronale * LB_FLEXI.cotisationPatronale)
  return {
    flexiSalaireBrut: salaire,
    flexiPecule,
    baseONSSPatronale,
    cotisationONSSpatronale,
    coutEmployeur: round2(baseONSSPatronale + cotisationONSSpatronale),
    netTravailleur: baseONSSPatronale,
  }
}

/**
 * Ce que la DmfA déclare pour un flexi-job sur un trimestre (23/09/2026).
 *
 * ✅ Instructions administratives ONSS 2026/3, lues le 23/09/2026 via
 * lire-source.yml (sondé le 23/09/2026, 21 variantes, verdict trouve) :
 *  - flexi_and_overtime_horeca.html, « Informations complémentaires DmfA » :
 *    codes travailleur « 050 type 0 » (manuels) et « 450 type 0 »
 *    (intellectuels) ; rémunérations en code « 22 pour les rémunérations
 *    flexi-jobs » et « 23 pour les primes et avantages […] qui ne se rapportent
 *    pas directement aux prestations du trimestre » ; « aucune réduction de
 *    cotisations sociales » ;
 *  - même page : « la cotisation de 28,00 % s'applique au salaire complet (y
 *    compris le pécule de vacances) », et les codes rémunération classiques du
 *    pécule de vacances ne s'appliquent pas aux flexis — le flexi-pécule, payé
 *    avec le salaire, se déclare donc avec lui, en code 22 ;
 *  - notion_flexi.html : la zone FlexiNotion « FL » est réservée aux flexis
 *    déclarés comme travailleurs ORDINAIRES faute de Dimona flexi à temps (ou
 *    période Dimona ≠ période DmfA). Un flexi déclaré en 050/450 ne la porte pas.
 *
 * Hypothèse d'entrée, écrite parce qu'elle décide du montant : `salaireTrimestre`
 * est le flexi-salaire SANS pécule — c'est ce que portent aujourd'hui les fiches
 * (le générateur de fiches n'ajoute pas de flexi-pécule au brut). Si un jour le
 * brut des fiches inclut le pécule, cette fonction le compterait deux fois.
 *
 * @param {number} salaireTrimestre flexi-salaire du trimestre, pécule non compris
 * @param {{ ouvrier?: boolean }} [opts]
 */
export function declarationFlexiDmfa(salaireTrimestre, { ouvrier = false } = {}) {
  const f = coutFlexiDepuisSalaire(salaireTrimestre)
  const workerCode = ouvrier ? CODES_PAR_REGIME.flexijob.ouvrier : CODES_PAR_REGIME.flexijob.employe
  return {
    workerCode,
    base: f.baseONSSPatronale,
    cotisation: f.cotisationONSSpatronale,
    remunerations: f.baseONSSPatronale > 0 ? [{ code: '22', amount: f.baseONSSPatronale }] : [],
    contributions: [{ code: workerCode, type: '0', basis: f.baseONSSPatronale, amount: f.cotisationONSSpatronale }],
  }
}

/**
 * Calcule la paie d'un flexi-jobber pour une prestation donnée.
 *
 * Le flexi-salaire est exonéré ONSS travailleur + PP jusqu'au plafond annuel.
 * Seule la cotisation patronale spéciale de 28% est due par l'employeur
 * sur la base (flexi-salaire + flexi-pécule de vacances 7,67%).
 *
 * @param {number} heures - Heures prestées
 * @param {number} flexiSalaireHoraire - € / heure
 * @param {Object} [options]
 * @param {string} [options.cp='302'] - Commission paritaire
 * @param {number} [options.cumulAnnuelFlexi=0] - Cumul brut flexi déjà perçu sur l'année
 * @param {boolean} [options.pensionne=false]
 * @param {boolean} [options.pensionAnticipee=false]
 * @returns {Object} Détail du calcul + détection dépassement plafond
 */
export function calcPayrollFlexi(heures, flexiSalaireHoraire, options = {}) {
  const {
    cp = '302',
    cumulAnnuelFlexi = 0,
    pensionne = false,
    pensionAnticipee = false,
  } = options

  // Validation entrées
  if (typeof heures !== 'number' || heures <= 0) {
    throw new Error('calcPayrollFlexi: heures doit être un nombre > 0')
  }
  if (typeof flexiSalaireHoraire !== 'number' || flexiSalaireHoraire <= 0) {
    throw new Error('calcPayrollFlexi: flexiSalaireHoraire doit être un nombre > 0')
  }

  // 1 à 6 — la règle vit dans coutFlexiDepuisSalaire(), une seule fois.
  const {
    flexiSalaireBrut, flexiPecule, baseONSSPatronale,
    cotisationONSSpatronale, coutEmployeur, netTravailleur,
  } = coutFlexiDepuisSalaire(heures * flexiSalaireHoraire)

  // 7. Vérification plafond fiscal annuel
  const nouveauCumul = round2(cumulAnnuelFlexi + flexiSalaireBrut + flexiPecule)
  const depassementPlafond = verifDepassementPlafond({
    nouveauCumul,
    pensionne,
    pensionAnticipee,
  })

  return {
    heures,
    flexiSalaireHoraire,
    flexiSalaireBrut,
    flexiPecule,
    baseONSSPatronale,
    cotisationONSSpatronale,
    coutEmployeur,
    netTravailleur,
    cumulAnnuelAvant: round2(cumulAnnuelFlexi),
    cumulAnnuelApres: nouveauCumul,
    depassementPlafond,
    regime: pensionne
      ? pensionAnticipee
        ? 'flexi_pension_anticipee'
        : 'flexi_pension_normale'
      : 'flexi_standard',
    cp,
    derniereMAJConstantes: LB_FLEXI._meta.dateMAJ,
  }
}

/**
 * Vérifie si le cumul annuel dépasse le plafond fiscal applicable.
 */
function verifDepassementPlafond({ nouveauCumul, pensionne, pensionAnticipee }) {
  // Pension normale : pas de plafond
  if (pensionne && !pensionAnticipee) {
    return null
  }

  // Pension anticipée : plafond spécifique
  if (pensionne && pensionAnticipee) {
    const plafond = LB_FLEXI.plafondAnnuelPensionnesAnticipes
    if (nouveauCumul > plafond) {
      return {
        plafond,
        nouveauCumul,
        montantTaxable: round2(nouveauCumul - plafond),
        message: `⚠️ Pensionné anticipé : cumul annuel ${nouveauCumul}€ > ${plafond}€. Risque récupération pension par SPF Finances / SFP.`,
        actionRequise: 'Alerter travailleur et consulter SFP',
      }
    }
    return null
  }

  // Non-pensionné : plafond 18 000€ (2026)
  const plafond = LB_FLEXI.plafondAnnuelNonPensionnes
  if (nouveauCumul > plafond) {
    return {
      plafond,
      nouveauCumul,
      montantTaxable: round2(nouveauCumul - plafond),
      message: `⚠️ Cumul annuel flexi ${nouveauCumul}€ > ${plafond}€. Montant excédentaire soumis au précompte professionnel normal et à déclarer en code 250 sur la fiche 281.10.`,
      actionRequise: 'Appliquer PP sur excédent + déclarer code 250 Belcotax',
    }
  }

  return null
}

/* ============================================================
 * 4. HELPERS
 * ============================================================ */


/**
 * Convertit un brut mensuel temps plein en taux horaire
 * (base 38h/semaine par défaut = 164,666 h/mois).
 */
export function brutMensuelVersHoraire(brutMensuel, heuresParSemaine = 38) {
  const heuresParMois = (heuresParSemaine * 52) / 12
  return round2(brutMensuel / heuresParMois)
}
